/*
 * Catch-all for every case URL that is not the workspace. The IDE replaced the
 * old case-prep pages (documents, analysis, drafts, intake forms, case
 * settings...), so any of those links now lands in the workspace, opening the
 * draft, form or settings the URL named where it can.
 */
import type { FC } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { CASE_HOME_SEGMENT, legacyCasePathToWorkspace } from "./routes";
import { useCaseRoute } from "./useCaseRoute";

/**
 * `target` with the current URL's query and hash carried over, so a deep link
 * (`?open=draft:…&thread=…`) survives the redirect. The target's own params win.
 */
export function withLocationQuery(
  target: string,
  search: string,
  hash: string
): string {
  const [beforeHash, targetHash = ""] = target.split("#", 2);
  const [path, targetQuery = ""] = beforeHash.split("?", 2);
  const params = new URLSearchParams(search);
  new URLSearchParams(targetQuery).forEach((value, key) =>
    params.set(key, value)
  );
  const query = params.toString();
  const fragment = targetHash ? `#${targetHash}` : hash;
  return `${path}${query ? `?${query}` : ""}${fragment}`;
}

export const CaseWorkspaceRedirect: FC = () => {
  const { pathname, search, hash } = useLocation();
  const route = useCaseRoute();
  if (!route.individualId) return null;
  const ref = {
    individualId: route.individualId,
    foreignNationalId: route.foreignNationalId
  };
  const target =
    legacyCasePathToWorkspace(pathname, ref) ?? route.workspacePath();
  return <Navigate to={withLocationQuery(target, search, hash)} replace />;
};

/** A case's index URL opens the workspace, keeping the query and hash. */
export const CaseIndexRedirect: FC = () => {
  const { search, hash } = useLocation();
  return (
    <Navigate to={{ pathname: CASE_HOME_SEGMENT, search, hash }} replace />
  );
};
